import {
  forwardRef,
  useContext,
  PropsWithoutRef,
  Ref,
  CSSProperties,
  ReactElement,
  useRef,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper, PopperProps } from 'react-popper';
import { VirtualElement } from '@popperjs/core';
import { OffsetModifier } from '@popperjs/core/lib/modifiers/offset';

import { BaseProps, ForwardProps } from '../../types';
import { useConfiguration, useConsolidatedRef, useElement, useUID } from '../../hooks';
import { windowIsAvailable } from '../../utils';

import { PopoverManagerContext } from './PopoverContext';
import { StyledPopover, StyledPopoverArrow } from './Popover.styles';

export type Delay = 'none' | 'short' | 'long';
export interface PopoverProps<Modifiers extends string = string> extends BaseProps {
  /** The DOM element or [virtual element](https://popper.js.org/docs/v2/virtual-elements) that the Popover is positioned in reference to. */
  target: Element | VirtualElement | null;
  /**
   * If true, the Popover will be visible.
   * @default true
   */
  show?: boolean;
  /**
   * If true, the Popover will render outside of the current DOM hierarchy in a [portal](https://reactjs.org/docs/portals.html).
   * @default false
   */
  portal?: boolean;
  /**
   * Delay on showing the Popover.
   * @default "none"
   */
  showDelay?: Delay;
  /**
   * Delay on hiding the Popover.
   * @default "none"
   */
  hideDelay?: Delay;
  /**
   * [React Popper Placement](https://popper.js.org/react-popper/v2/render-props/#placement)
   */
  placement?: PopperProps<Modifiers>['placement'];
  /**
   * [React Popper Strategy](https://popper.js.org/react-popper/v2/render-props/#strategy)
   * @default "absolute"
   */
  strategy?: PopperProps<Modifiers>['strategy'];
  /**
   * [React Popper Modifiers](https://popper.js.org/react-popper/v2/render-props/#modifiers)
   * @default []
   */
  modifiers?: PopperProps<Modifiers>['modifiers'];
  /**
   * If true, an arrow will visually identify from which element the Popover is rendered.
   * @default false
   */
  arrow?: boolean;
  /** The group that the Popover belongs to. Popovers in the same group will be closed when others open. */
  groupId?: string;
  /**
   * If true, the Popover will be visibly hidden when its target leaves the viewport.
   * @default false
   */
  hideOnTargetHidden?: boolean;
  /** The content of the Popover. */
  children: ReactNode;
  /** The ref of the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

const delays = {
  none: 0,
  short: 500,
  long: 1000
};

const Popover: <Modifiers extends string = string>(
  props: PopoverProps<Modifiers> & ForwardProps
) => ReactElement | null = forwardRef(
  (
    {
      show = true,
      portal = false,
      target,
      placement,
      strategy = 'fixed',
      style,
      modifiers = [],
      arrow = false,
      groupId,
      showDelay = 'none',
      hideDelay = 'none',
      hideOnTargetHidden = false,
      children,
      ...restProps
    }: PropsWithoutRef<PopoverProps> & { style?: CSSProperties },
    ref: PopoverProps['ref']
  ) => {
    const { portalTarget } = useConfiguration();
    const { checkActive, setActive } = useContext(PopoverManagerContext);
    const [, setPopperEl] = useElement<HTMLDivElement>();
    const popperRef = useConsolidatedRef<HTMLDivElement>(ref, setPopperEl);
    const arrowRef = useRef<HTMLDivElement>(null);
    const forceUpdateRef = useRef<() => void>();
    const [showPopover, setShowPopover] = useState(show);
    const timeout = useRef<number>();
    const uid = useUID();

    const memoedModifiers = useMemo(
      () => [
        {
          name: 'offset',
          options: {
            offset: [0, arrow ? 5.5 : 2]
          }
        },
        { name: 'hide', enabled: hideOnTargetHidden },
        { name: 'arrow', enabled: arrow, options: { element: arrowRef.current } },
        {
          name: 'preventOverflow',
          options: {
            tether: !modifiers.find(m => m.name === 'sameWidth')
          }
        },
        {
          name: 'computeStyles',
          options: {
            gpuAcceleration: false
          }
        },
        ...modifiers
      ],
      [modifiers, arrow, hideOnTargetHidden]
    );

    const offset = useMemo(() => {
      const offsetModifier = [...memoedModifiers]
        .reverse()
        .find(m => m.name === 'offset' && m.enabled !== false) as OffsetModifier | undefined;
      return Array.isArray(offsetModifier?.options?.offset)
        ? offsetModifier?.options?.offset[1] ?? 0
        : 0;
    }, [memoedModifiers]);

    const { styles, attributes, forceUpdate } = usePopper(target, popperRef.current, {
      placement,
      strategy,
      modifiers: memoedModifiers
    });

    if (forceUpdate) forceUpdateRef.current = forceUpdate;

    useEffect(() => {
      const observer = new ResizeObserver(() => {
        forceUpdateRef.current?.();
      });

      if (popperRef.current) observer.observe(popperRef.current);
      if (target instanceof Element) observer.observe(target);

      return () => {
        observer.disconnect();
      };
    }, [forceUpdate, popperRef.current, target]);

    useEffect(() => {
      if (groupId && show) setActive(uid, groupId);

      if (windowIsAvailable) {
        if (timeout.current) clearTimeout(timeout.current);
        timeout.current = window.setTimeout(() => {
          setShowPopover(show);
        }, delays[show ? showDelay : hideDelay]);
      } else {
        setShowPopover(show);
      }
    }, [show]);

    useEffect(() => {
      return () => clearTimeout(timeout.current);
    }, []);

    const content = (
      <StyledPopover
        {...restProps}
        offset={offset}
        ref={popperRef}
        style={{ ...style, ...styles.popper }}
        {...attributes.popper}
      >
        {arrow && <StyledPopoverArrow ref={arrowRef} style={{ ...styles.arrow }} />}
        {children}
      </StyledPopover>
    );

    if (!target || !showPopover || (groupId && !checkActive(uid, groupId))) return null;

    return portal && portalTarget ? createPortal(content, portalTarget) : content;
  }
);

export default Popover;
